import PropTypes from 'prop-types';
import {memo, useMemo} from 'react';

import {ROLES_NAMES} from '@/src/app/constants/roles';
import useIsMobile from '@/src/hooks/useIsMobile';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Rating, SectionContainer, TagList, Text} from '../../../ui';
import css from '../../styles/CourseTopInfo.module.css';
import UserInfo from './UserInfo';

const CourseTopInfo = ({
  topicsCount,
  courseTitle,
  courseDescription,
  courseCategories,
  rating,
  updateDate,
  difficultyLevel,
  courseCreator,
  ratingCount,
  surgeryDate,
  coAuthors,
  isPublic,
}) => {
  const {isMaxWidth600} = useIsMobile();

  const isClinic = useMemo(
    () => courseCreator?.type === ROLES_NAMES.CLINIC,
    [courseCreator?.type]
  );

  const {CLINIC_TARGET_PROFILE, USER_PROFILE} = useRoutePaths();

  const courseCreatorLink = isClinic
    ? CLINIC_TARGET_PROFILE.getLink(courseCreator.slug)
    : USER_PROFILE.getLink(courseCreator.slug);

  return (
    <div className={css.wrapper}>
      <SectionContainer>
        <div className={css.main}>
          {isPublic && (
            <div className={css.freeBadge}>
              <TextLocal id={'search.availabilityFree'} />
            </div>
          )}
          <p className={css.courseCount}>
            <Text
              tagComponent={Text.TAG.SPAN}
              fontFamily={Text.FONT_FAMILY.POPPINS}
              fontWeight={
                isMaxWidth600 ? Text.FONT_WEIGHT.THIN : Text.FONT_WEIGHT.MEDIUM
              }
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              fontSize={
                isMaxWidth600 ? Text.FONT_SIZE.SIZE_12 : Text.FONT_SIZE.SIZE_16
              }
              color={Text.COLOR.WHITE}
            >
              {topicsCount}{' '}
              <TextLocal
                id={
                  topicsCount > 1
                    ? 'courseDetails.topics'
                    : 'courseDetails.topic'
                }
              />{' '}
              | <TextLocal id={difficultyLevel.label} />
              {isMaxWidth600 && (
                <>
                  {' '}
                  |{' '}
                  <TextLocal
                    id={'courseDetails.updateDate'}
                    params={{date: updateDate}}
                  />
                </>
              )}
            </Text>
            {isMaxWidth600 && surgeryDate && (
              <Text
                tagComponent={Text.TAG.SPAN}
                fontFamily={Text.FONT_FAMILY.POPPINS}
                fontWeight={
                  isMaxWidth600
                    ? Text.FONT_WEIGHT.THIN
                    : Text.FONT_WEIGHT.MEDIUM
                }
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                fontSize={
                  isMaxWidth600
                    ? Text.FONT_SIZE.SIZE_12
                    : Text.FONT_SIZE.SIZE_16
                }
                color={Text.COLOR.WHITE}
              >
                <TextLocal id={'courseDetails.date-of-surgery'} />:{' '}
                {surgeryDate}
              </Text>
            )}
          </p>
          <h1 className={css.courseTitle}>
            <Text
              tagComponent={Text.TAG.SPAN}
              fontSize={Text.FONT_SIZE.SIZE_36}
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_2}
              fontWeight={Text.FONT_WEIGHT.MEDIUM}
              fontFamily={Text.FONT_FAMILY.RUBIK}
              transform={Text.TRANSFORM.CAPITALIZE}
            >
              {courseTitle}
            </Text>
          </h1>

          <div className={css.courseCreatorTop}>
            <UserInfo
              avatar={courseCreator.image}
              name={courseCreator.name}
              link={courseCreatorLink}
            />

            {Boolean(coAuthors?.length) && (
              <>
                &nbsp;
                {!isMaxWidth600 && <> | </>}
                <a
                  href={'#co-authors-section'}
                  className={css.coAuthorsLink}
                  aria-label={'anchore to co-authors'}
                >
                  +{coAuthors.length}{' '}
                  <TextLocal id={'courseDetails.co-authors'} />
                </a>
              </>
            )}
          </div>
          <div className={css.courseDescription}>
            <Text
              fontSize={Text.FONT_SIZE.SIZE_16}
              color={Text.COLOR.WHITE}
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_4}
            >
              {courseDescription}
            </Text>
          </div>

          <div className={css.courseCategories}>
            <TagList tags={courseCategories} color={TagList.COLOR.WHITE} />
          </div>

          <div className={css.courseRatingInfo}>
            {ratingCount ? (
              <>
                <Rating
                  rating={rating}
                  withMaxRating
                  color={Rating.COLOR.WHITE}
                  fontWeight={Rating.FONT_WEIGHT.NORMAL}
                />
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_14}
                  lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                  fontWeight={Text.FONT_WEIGHT.NORMAL}
                >
                  {ratingCount}{' '}
                  <TextLocal
                    id={
                      ratingCount > 1
                        ? 'courseDetails.ratings'
                        : 'courseDetails.rating'
                    }
                  />
                </Text>
              </>
            ) : (
              <Text
                fontSize={Text.FONT_SIZE.SIZE_14}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                fontWeight={Text.FONT_WEIGHT.NORMAL}
              >
                <TextLocal id={'courseDetails.write-the-first-review'} />
              </Text>
            )}
          </div>

          {!isMaxWidth600 && (
            <div>
              <Text
                fontSize={Text.FONT_SIZE.SIZE_14}
                fontWeight={Text.FONT_WEIGHT.THIN}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              >
                <TextLocal
                  id={'courseDetails.updateDate'}
                  params={{date: updateDate}}
                />
              </Text>
              {surgeryDate && (
                <Text
                  fontSize={Text.FONT_SIZE.SIZE_14}
                  fontWeight={Text.FONT_WEIGHT.THIN}
                  lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
                >
                  <TextLocal id={'courseDetails.date-of-surgery'} />:{' '}
                  {surgeryDate}
                </Text>
              )}
            </div>
          )}
        </div>
      </SectionContainer>
    </div>
  );
};

CourseTopInfo.propTypes = {
  topicsCount: PropTypes.number.isRequired,
  ratingCount: PropTypes.number.isRequired,
  courseTitle: PropTypes.string.isRequired,
  courseDescription: PropTypes.string.isRequired,
  courseCategories: PropTypes.array.isRequired,
  rating: PropTypes.number.isRequired,
  updateDate: PropTypes.string.isRequired,
  difficultyLevel: PropTypes.object.isRequired,
  courseCreator: PropTypes.object.isRequired,
  surgeryDate: PropTypes.string.isRequired,
  coAuthors: PropTypes.array.isRequired,
  isPublic: PropTypes.bool,
};

export default memo(CourseTopInfo);
